import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { supabase } from '../../lib/supabase';
import PrivateImage from '../../components/common/PrivateImage';
import { 
  ArrowLeft,
  Edit2,
  User,
  Calendar,
  Phone,
  Mail,
  MapPin,
  Shield,
  Heart,
  FileText,
  UserCircle,
  Users,
  Activity,
  AlertCircle,
  CheckCircle,
  XCircle
} from 'lucide-react';

const PlayerDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const [player, setPlayer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchPlayer();
  }, [id]);

  const fetchPlayer = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('players')
        .select('*')
        .eq('id', id)
        .single();

      if (error) throw error;

      if (!data) {
        setError('Jugador no encontrado');
        return;
      }

      setPlayer(data);
    } catch (error) {
      console.error('Error al cargar jugador:', error);
      setError('Error al cargar el jugador');
    } finally {
      setLoading(false);
    }
  };

  const calculateAge = (birthDate) => {
    const today = new Date();
    const birth = new Date(birthDate);
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const getTeamColor = (team) => {
    switch(team) {
      case 'Juvenil':
        return 'bg-blue-600';
      case 'Cadete':
        return 'bg-green-600';
      case 'Amateur':
        return 'bg-purple-600';
      default:
        return 'bg-gray-600';
    }
  };

  const isExpiringSoon = (dateString) => {
    if (!dateString) return false;
    const date = new Date(dateString);
    const today = new Date();
    const diffTime = date - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays <= 30 && diffDays >= 0;
  };

  const isExpired = (dateString) => {
    if (!dateString) return false;
    const date = new Date(dateString);
    const today = new Date();
    return date < today;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Cargando jugador...</p>
        </div>
      </div>
    );
  }

  if (error || !player) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Error</h2>
          <p className="text-gray-600 mb-6">{error || 'Jugador no encontrado'}</p>
          <button
            onClick={() => navigate('/players')}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Volver a Jugadores
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/players')}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5 text-gray-600" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Ficha del Jugador</h1>
                <p className="text-sm text-gray-600">Información completa</p>
              </div>
            </div>

            {(userProfile?.role === 'admin' || userProfile?.role === 'entrenador') && (
              <button
                onClick={() => navigate(`/players/edit/${player.id}`)}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Edit2 className="w-4 h-4" />
                Editar
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-xl shadow-sm overflow-hidden mb-8">
          <div className={`h-32 ${getTeamColor(player.team)}`}></div>
          
          <div className="px-8 pb-8">
            <div className="flex flex-col md:flex-row md:items-end gap-6 -mt-16">
              <div className="relative">
                {player.photo_url ? (
                  <PrivateImage
                    bucket="player-photos"
                    path={player.photo_url}
                    alt={`${player.first_name} ${player.last_name}`}
                    className="w-32 h-32 rounded-full object-cover border-4 border-white shadow-lg"
                    fallbackIcon={UserCircle}
                  />
                ) : (
                  <div className="w-32 h-32 rounded-full bg-gray-200 flex items-center justify-center border-4 border-white shadow-lg">
                    <UserCircle className="w-20 h-20 text-gray-400" />
                  </div>
                )}
                {player.player_number && (
                  <div className="absolute -bottom-2 -right-2 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center text-xl font-bold shadow-lg">
                    {player.player_number}
                  </div>
                )}
              </div>

              <div className="flex-1">
                <h2 className="text-3xl font-bold text-gray-900 mb-2">
                  {player.first_name} {player.last_name}
                </h2>
                
                <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600 mb-4">
                  {player.position && (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 text-gray-800 rounded-full font-medium">
                      <Shield className="w-4 h-4" />
                      {player.position}
                    </span>
                  )}
                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-100 text-blue-800 rounded-full font-medium">
                    <Users className="w-4 h-4" />
                    {player.team}
                  </span>
                  {player.date_of_birth && (
                    <span className="inline-flex items-center gap-1.5">
                      <Calendar className="w-4 h-4" />
                      {calculateAge(player.date_of_birth)} años
                    </span>
                  )}
                </div>

                <div className="flex flex-wrap gap-6 text-sm">
                  {player.phone && (
                    <div className="flex items-center gap-2 text-gray-600">
                      <Phone className="w-4 h-4" />
                      <span>{player.phone}</span>
                    </div>
                  )}
                  {player.email && (
                    <div className="flex items-center gap-2 text-gray-600">
                      <Mail className="w-4 h-4" />
                      <span>{player.email}</span>
                    </div>
                  )}
                  {(player.city || player.address) && (
                    <div className="flex items-center gap-2 text-gray-600">
                      <MapPin className="w-4 h-4" />
                      <span>{player.city || player.address}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-8">
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                <User className="w-5 h-5 text-blue-600" />
                Datos Personales
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-600">Fecha de nacimiento</p>
                  <p className="font-semibold text-gray-900">{formatDate(player.date_of_birth)}</p>
                </div>
                {player.dni && (
                  <div>
                    <p className="text-sm text-gray-600">DNI/NIE</p>
                    <p className="font-semibold text-gray-900">{player.dni}</p>
                  </div>
                )}
                {player.address && (
                  <div className="md:col-span-2">
                    <p className="text-sm text-gray-600">Dirección completa</p>
                    <p className="font-semibold text-gray-900">
                      {player.address}
                      {player.postal_code && `, ${player.postal_code}`}
                      {player.city && `, ${player.city}`}
                    </p>
                  </div>
                )}
              </div>

              {player.guardian_name && (
                <div className="mt-6 pt-6 border-t">
                  <h4 className="font-semibold text-gray-900 mb-3">Tutor Legal</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-600">Nombre</p>
                      <p className="font-semibold text-gray-900">{player.guardian_name}</p>
                    </div>
                    {player.guardian_relationship && (
                      <div>
                        <p className="text-sm text-gray-600">Relación</p>
                        <p className="font-semibold text-gray-900">{player.guardian_relationship}</p>
                      </div>
                    )}
                    {player.guardian_phone && (
                      <div>
                        <p className="text-sm text-gray-600">Teléfono</p>
                        <p className="font-semibold text-gray-900">{player.guardian_phone}</p>
                      </div>
                    )}
                    {player.guardian_email && (
                      <div>
                        <p className="text-sm text-gray-600">Email</p>
                        <p className="font-semibold text-gray-900">{player.guardian_email}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="bg-white rounded-xl shadow-sm p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                <Shield className="w-5 h-5 text-blue-600" />
                Datos Deportivos
              </h3>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {player.player_number && (
                  <div>
                    <p className="text-sm text-gray-600">Dorsal</p>
                    <p className="text-2xl font-bold text-blue-600">{player.player_number}</p>
                  </div>
                )}
                {player.position && (
                  <div>
                    <p className="text-sm text-gray-600">Posición</p>
                    <p className="font-semibold text-gray-900">{player.position}</p>
                  </div>
                )}
                {player.preferred_foot && (
                  <div>
                    <p className="text-sm text-gray-600">Pie</p>
                    <p className="font-semibold text-gray-900">{player.preferred_foot}</p>
                  </div>
                )}
                {player.height && (
                  <div>
                    <p className="text-sm text-gray-600">Altura</p>
                    <p className="font-semibold text-gray-900">{player.height} cm</p>
                  </div>
                )}
                {player.weight && (
                  <div>
                    <p className="text-sm text-gray-600">Peso</p>
                    <p className="font-semibold text-gray-900">{player.weight} kg</p>
                  </div>
                )}
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                <Heart className="w-5 h-5 text-red-600" />
                Datos Médicos
              </h3>
              
              <div className="space-y-4">
                {player.blood_type && (
                  <div>
                    <p className="text-sm text-gray-600">Grupo sanguíneo</p>
                    <p className="font-semibold text-gray-900">{player.blood_type}</p>
                  </div>
                )}
                {player.allergies && (
                  <div>
                    <p className="text-sm text-gray-600">Alergias</p>
                    <p className="font-semibold text-gray-900">{player.allergies}</p>
                  </div>
                )}
                {player.medical_conditions && (
                  <div>
                    <p className="text-sm text-gray-600">Condiciones médicas</p>
                    <p className="font-semibold text-gray-900">{player.medical_conditions}</p>
                  </div>
                )}
                {player.emergency_contact && (
                  <div className="pt-4 border-t">
                    <h4 className="font-semibold text-gray-900 mb-3">Contacto de Emergencia</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-gray-600">Nombre</p>
                        <p className="font-semibold text-gray-900">{player.emergency_contact}</p>
                      </div>
                      {player.emergency_phone && (
                        <div>
                          <p className="text-sm text-gray-600">Teléfono</p>
                          <p className="font-semibold text-gray-900">{player.emergency_phone}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {player.notes && (
              <div className="bg-white rounded-xl shadow-sm p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <FileText className="w-5 h-5 text-gray-600" />
                  Notas Adicionales
                </h3>
                <p className="text-gray-700 whitespace-pre-wrap">{player.notes}</p>
              </div>
            )}
          </div>

          <div className="space-y-8">
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                <FileText className="w-5 h-5 text-blue-600" />
                Documentación
              </h3>
              
              <div className="space-y-4">
                <div className="p-4 border border-gray-200 rounded-lg">
                  <div className="flex items-start justify-between mb-2">
                    <h4 className="font-semibold text-gray-900">Licencia Federativa</h4>
                    {player.license_expiry && (
                      <>
                        {isExpired(player.license_expiry) ? (
                          <XCircle className="w-5 h-5 text-red-600" />
                        ) : isExpiringSoon(player.license_expiry) ? (
                          <AlertCircle className="w-5 h-5 text-orange-600" />
                        ) : (
                          <CheckCircle className="w-5 h-5 text-green-600" />
                        )}
                      </>
                    )}
                  </div>
                  {player.license_number && (
                    <p className="text-sm text-gray-600 mb-1">
                      Nº {player.license_number}
                    </p>
                  )}
                  {player.license_expiry && (
                    <p className={`text-sm font-medium ${
                      isExpired(player.license_expiry) ? 'text-red-600' :
                      isExpiringSoon(player.license_expiry) ? 'text-orange-600' :
                      'text-green-600'
                    }`}>
                      {isExpired(player.license_expiry) ? '❌ Caducada' :
                       isExpiringSoon(player.license_expiry) ? '⚠️ Caduca pronto' :
                       '✓ Vigente'} - {formatDate(player.license_expiry)}
                    </p>
                  )}
                  {!player.license_number && !player.license_expiry && (
                    <p className="text-sm text-gray-500">No registrada</p>
                  )}
                </div>

                <div className="p-4 border border-gray-200 rounded-lg">
                  <div className="flex items-start justify-between mb-2">
                    <h4 className="font-semibold text-gray-900">Seguro Deportivo</h4>
                    {player.insurance_expiry && (
                      <>
                        {isExpired(player.insurance_expiry) ? (
                          <XCircle className="w-5 h-5 text-red-600" />
                        ) : isExpiringSoon(player.insurance_expiry) ? (
                          <AlertCircle className="w-5 h-5 text-orange-600" />
                        ) : (
                          <CheckCircle className="w-5 h-5 text-green-600" />
                        )}
                      </>
                    )}
                  </div>
                  {player.insurance_number && (
                    <p className="text-sm text-gray-600 mb-1">
                      Póliza {player.insurance_number}
                    </p>
                  )}
                  {player.insurance_expiry && (
                    <p className={`text-sm font-medium ${
                      isExpired(player.insurance_expiry) ? 'text-red-600' :
                      isExpiringSoon(player.insurance_expiry) ? 'text-orange-600' :
                      'text-green-600'
                    }`}>
                      {isExpired(player.insurance_expiry) ? '❌ Caducado' :
                       isExpiringSoon(player.insurance_expiry) ? '⚠️ Caduca pronto' :
                       '✓ Vigente'} - {formatDate(player.insurance_expiry)}
                    </p>
                  )}
                  {!player.insurance_number && !player.insurance_expiry && (
                    <p className="text-sm text-gray-500">No registrado</p>
                  )}
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                <Activity className="w-5 h-5 text-green-600" />
                Estado
              </h3>
              
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Estado del jugador</span>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                    player.active 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {player.active ? 'Activo' : 'Inactivo'}
                  </span>
                </div>
                {player.joined_date && (
                  <div>
                    <p className="text-sm text-gray-600">Fecha de alta</p>
                    <p className="font-semibold text-gray-900">{formatDate(player.joined_date)}</p>
                  </div>
                )}
                {player.left_date && (
                  <div>
                    <p className="text-sm text-gray-600">Fecha de baja</p>
                    <p className="font-semibold text-gray-900">{formatDate(player.left_date)}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlayerDetail;